import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { CreateUserVoiceRequestData } from "@/domain/entities/vistassistant/TicketTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ConversationBubble from "./ConversationBubble";

type UserVoiceResponseProps = {
    data: CreateUserVoiceRequestData;
    onSubmit: (data: CreateUserVoiceRequestData) => Promise<void>;
};

const UserVoiceResponse = ({ data, onSubmit }: UserVoiceResponseProps) => {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await onSubmit(data);
            setSubmitted(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <ConversationBubble>
                <p className="text-sm text-green-600">
                    {t("vistassistant.userVoice.submitted")}
                </p>
            </ConversationBubble>
        );
    }

    return (
        <ConversationBubble>
            <div className="flex flex-col gap-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                    <Badge className="bg-blue-500 text-white">
                        {t("vistassistant.userVoice.badge")}
                    </Badge>
                    <Badge className="border border-border bg-transparent text-foreground">
                        {data.feature}
                    </Badge>
                </div>

                <p className="font-medium">{data.title}</p>

                {data.description && (
                    <p className="text-muted-foreground">{data.description}</p>
                )}

                {data.similarTickets && data.similarTickets.length > 0 && (
                    <div className="rounded border border-blue-200 bg-blue-50 p-2 dark:border-blue-800 dark:bg-blue-950">
                        <p className="mb-1 text-xs font-medium text-blue-700 dark:text-blue-300">
                            {t("vistassistant.userVoice.similarTickets", {
                                count: data.similarTickets.length,
                            })}
                        </p>
                        {data.similarTickets.map((ticket) => (
                            <p
                                key={ticket.id}
                                className="text-xs text-blue-600 dark:text-blue-400"
                            >
                                • {ticket.title}{" "}
                                {ticket.votes !== undefined &&
                                    `(${ticket.votes} votes)`}
                            </p>
                        ))}
                    </div>
                )}

                <Button
                    className="w-fit bg-blue-500 hover:bg-blue-600"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                    size="sm"
                >
                    {isSubmitting
                        ? t("vistassistant.userVoice.submitting")
                        : t("vistassistant.userVoice.submit")}
                </Button>
            </div>
        </ConversationBubble>
    );
};

export default UserVoiceResponse;
